import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { localDateString, timezoneAbbr } from '../lib/timezones';
import { reportCacheConfig } from '../lib/reportCache';
import { ReportShell } from './reports/ReportView';
import { MonthlyReportView, type MonthlyReportData } from './reports/MonthlyReportView';
import { exportDomAsHtml, exportDomAsPdf, slugify } from '../lib/exportDomReport';
import { fmtTs } from '../lib/globalTz';
import { ThreatHuntingReportView } from './reports/ThreatHuntingReportView';

interface Props { customerId?: string }

type ReportMode = 'soc' | 'hunt';

interface SnapshotMeta {
  month: string;
  source: 'auto' | 'manual' | 'backfill';
  totalIncidents: number;
  reconciled: 'yes' | 'no' | 'unknown';
  updatedAt: string;
}

/**
 * Monthly SOC Report tab. Fetches the (independent) `/monthly-report`
 * endpoint which transcribes the customer's "Monthly_SOC_Report" Logic App,
 * then renders the dedicated <MonthlyReportView /> - a structurally distinct
 * layout from the daily and weekly reports (adds month-over-month deltas,
 * daily trend, SLA-by-severity, identity-lifecycle and a capped incident
 * detail list).
 *
 * The download buttons (`lib/exportDomReport.ts`) clone the on-screen DOM
 * with all page CSS inlined for both the HTML snapshot and the PDF (print)
 * export, rather than a templated redraw - matches what the customer
 * actually sees on screen, fonts and recharts SVGs included.
 *
 * The "Save snapshot" action persists the current month's payload to the
 * `monthly_report_snapshots` table so historical numbers don't drift when
 * Sentinel later prunes/merges incidents. The auto-scheduler does this for
 * the previous month on the 1st of every month at 09:00 SGT; this button is
 * the manual fallback / backfill path.
 */
export function MonthlyReportTab({ customerId: customerIdProp }: Props = {}) {
  const { activeCustomerId, activeCustomer, customers } = useCustomers();
  const customerId = customerIdProp ?? activeCustomerId;
  const customer = customerIdProp
    ? customers.find((c) => c.id === customerIdProp)
    : activeCustomer;

  const [mode, setMode] = useState<ReportMode>('soc');

  const customerTz = customer?.timezone ?? 'UTC';

  // Compute "this month" and "last month" in the customer's timezone. Default
  // the picker to last month - the most recent fully-closed reporting period.
  const { thisMonth, lastMonth } = useMemo(() => {
    const today = localDateString(new Date(), customerTz); // YYYY-MM-DD
    const [y, m] = today.split('-').map(Number);
    const tm = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`;
    const py = m === 1 ? y! - 1 : y!;
    const pm = m === 1 ? 12 : m! - 1;
    const lm = `${String(py).padStart(4, '0')}-${String(pm).padStart(2, '0')}`;
    return { thisMonth: tm, lastMonth: lm };
  }, [customerTz]);

  const [month, setMonth] = useState<string>(lastMonth);
  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotMessage, setSnapshotMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setMonth(lastMonth);
  }, [lastMonth]);

  const monthlyQuery = useQuery<MonthlyReportData, Error & { hint?: string }>({
    queryKey: ['monthly-report', customerId, month],
    queryFn: () => apiFetch<MonthlyReportData>(
      `/monthly-report?customerId=${encodeURIComponent(customerId ?? '')}&month=${encodeURIComponent(month)}`,
    ),
    enabled: !!customerId && customerId !== 'default' && mode === 'soc',
    ...reportCacheConfig('monthly', month, customerTz),
  });
  const report = monthlyQuery.data ?? null;
  const loading = monthlyQuery.isLoading || monthlyQuery.isFetching;
  const error: string | null = (!customerId || customerId === 'default')
    ? 'Pick a specific customer to generate the monthly report. The MSSP "default" workspace is not supported here.'
    : monthlyQuery.error
      ? (monthlyQuery.error.hint ? `${monthlyQuery.error.message}. ${monthlyQuery.error.hint}` : monthlyQuery.error.message)
      : null;

  // Fetch the saved-snapshot list so we can show a "saved" badge next to the
  // picker when this month is already persisted. Refreshes after every save.
  const refreshSnapshots = useCallback(() => {
    if (!customerId || customerId === 'default') { setSnapshots([]); return; }
    apiFetch<{ snapshots: SnapshotMeta[] }>(
      `/monthly-report/snapshots?customerId=${encodeURIComponent(customerId)}`,
    )
      .then((r) => setSnapshots(r.snapshots ?? []))
      .catch(() => { /* non-fatal - UI just won't show the badge */ });
  }, [customerId]);

  useEffect(() => { refreshSnapshots(); }, [refreshSnapshots]);

  const reportTz = report?.timezone ?? customerTz;
  const tzAbbr = timezoneAbbr(reportTz);
  const customerName = customer?.name ?? customerId ?? 'Customer';
  const savedSnapshot = snapshots.find((s) => s.month === month);
  const isCurrentMonth = month === thisMonth;

  // Both HTML and PDF export clone the on-screen report DOM (with all page
  // CSS inlined) rather than redrawing it - customer asked for an artifact
  // that matches the dashboard view exactly (charts included). PDF reuses
  // the identical clone and triggers the browser's native print dialog.
  const exportOpts = useCallback(() => {
    const exportDateStr = new Date().toISOString().slice(0, 10);
    return {
      rootSelector: '.daily-report-root',
      title: `${customerName} - Monthly SOC Report - ${month}`,
      fileNameBase: `kloudynet-monthly-soc-report-${slugify(customerName)}-${month}-${exportDateStr}`,
      notMountedError: 'Monthly report content not visible - load a report first.',
    };
  }, [customerName, month]);

  const buildHtml = useCallback(async () => {
    if (!report) return { ok: false, error: 'Report not loaded.' };
    const exportedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    return exportDomAsHtml({
      ...exportOpts(),
      bannerHtml: `<div style="background:rgba(255,255,255,0.92);border-radius:12px;padding:14px 20px;margin-bottom:18px;color:#1e3c72;font-size:13px;box-shadow:0 4px 16px rgba(0,0,0,0.18);"><strong style="color:#0f172a;">${escapeHtml(customerName)}</strong> - Monthly SOC Report &middot; Reporting month: <strong>${escapeHtml(month)}</strong> &middot; Exported ${escapeHtml(exportedAt)} UTC</div>`,
    });
  }, [report, exportOpts, customerName, month]);

  const buildPdf = useCallback(async () => {
    if (!report) return { ok: false, error: 'Report not loaded.' };
    return exportDomAsPdf(exportOpts());
  }, [report, exportOpts]);

  const huntExportOpts = useCallback(() => {
    const exportDateStr = new Date().toISOString().slice(0, 10);
    return {
      rootSelector: '.threat-hunting-report-root',
      title: `${customerName} - Monthly Threat Hunting Report - ${month}`,
      fileNameBase: `kloudynet-monthly-threat-hunting-report-${slugify(customerName)}-${month}-${exportDateStr}`,
      notMountedError: 'Threat hunting report content not visible - load a report first.',
    };
  }, [customerName, month]);

  const buildHuntHtml = useCallback(async () => exportDomAsHtml(huntExportOpts()), [huntExportOpts]);
  const buildHuntPdf = useCallback(async () => exportDomAsPdf(huntExportOpts()), [huntExportOpts]);

  const saveSnapshot = useCallback(async () => {
    if (!customerId || customerId === 'default' || !report) return;
    setSavingSnapshot(true);
    setSnapshotMessage(null);
    try {
      await apiFetch<unknown>(`/monthly-report/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, month, source: 'manual' }),
      });
      setSnapshotMessage('Snapshot saved.');
      refreshSnapshots();
      void queryClient.invalidateQueries({ queryKey: ['monthly-report', customerId, month] });
      window.setTimeout(() => setSnapshotMessage(null), 4_000);
    } catch (e) {
      setSnapshotMessage(e instanceof Error ? `Save failed: ${e.message}` : 'Save failed.');
      window.setTimeout(() => setSnapshotMessage(null), 6_000);
    } finally {
      setSavingSnapshot(false);
    }
  }, [customerId, month, report, refreshSnapshots]);

  if (!customerId || customerId === 'default') {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-700 shadow-md">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Monthly SOC Report</h2>
        <p className="text-sm">Select a specific customer from the dropdown above to generate their monthly report.</p>
      </div>
    );
  }

  const snapshotButton = (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => { void saveSnapshot(); }}
        disabled={!report || loading || savingSnapshot}
        title={
          savedSnapshot
            ? `Last saved ${fmtTs(savedSnapshot.updatedAt)} (${savedSnapshot.source})`
            : 'Persist this month so historical numbers don\'t drift later'
        }
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {savingSnapshot ? 'Saving…' : savedSnapshot ? 'Re-save snapshot' : 'Save snapshot'}
      </button>
      {snapshotMessage && (
        <span className="text-[11px] text-slate-500">{snapshotMessage}</span>
      )}
      {!snapshotMessage && savedSnapshot && (
        <span className="text-[11px] text-emerald-700">
          Saved · {savedSnapshot.source}{isCurrentMonth ? ' · current month' : ''}
        </span>
      )}
    </div>
  );

  return (
    <>
      <div className="dr-no-print mb-3 flex gap-1 rounded-xl border border-border bg-background p-1 w-fit">
        <button
          type="button"
          onClick={() => setMode('soc')}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${mode === 'soc' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
        >
          SOC Report
        </button>
        <button
          type="button"
          onClick={() => setMode('hunt')}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${mode === 'hunt' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
        >
          Threat Hunting Report
        </button>
      </div>

      {mode === 'hunt' ? (
        <ReportShell
          toolbarTitle="Threat Hunting Report"
          customerName={customerName}
          controls={null}
          loading={false}
          error={null}
          printDisabled={false}
          buildHtml={buildHuntHtml}
          buildPdf={buildHuntPdf}
        >
          <ThreatHuntingReportView customerId={customerId} customerLogoUrl={customer?.logoUrl} period="monthly" />
        </ReportShell>
      ) : (
        <ReportShell
          toolbarTitle="Monthly SOC Report"
          customerName={customerName}
          controls={
            <label className="text-xs text-slate-600 dark:text-slate-300">
              <span className="block mb-1">Reporting month ({tzAbbr})</span>
              <input
                type="month"
                value={month}
                // Allow selecting up to "this month so far" - the report still
                // executes against the partial month if the user picks it.
                max={thisMonth}
                onChange={(e) => setMonth(e.target.value)}
                className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
              />
            </label>
          }
          extraToolbarActions={snapshotButton}
          buildHtml={buildHtml}
          loading={loading}
          error={error}
          printDisabled={!report || loading}
          buildPdf={buildPdf}
        >
          {report && !loading && (
            <MonthlyReportView report={report} customerName={customerName} />
          )}
        </ReportShell>
      )}
    </>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
